import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Alert, Tab, Tabs } from 'react-bootstrap';
import ParametersWithBadges from './ParametersWithBadges';
import { fetchProcessParameters, fetchQualityAttributes } from '../services/api';

const ParametersBadgeDemo = () => {
  const [processParameters, setProcessParameters] = useState([]);
  const [qualityAttributes, setQualityAttributes] = useState([]);
  const [selectedProcessParameters, setSelectedProcessParameters] = useState({});
  const [selectedQualityAttributes, setSelectedQualityAttributes] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeKey, setActiveKey] = useState('grouped');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('ParametersBadgeDemo: Loading data...');
      
      // Load process parameters
      const processData = await fetchProcessParameters();
      console.log('ParametersBadgeDemo: Process parameters loaded:', processData.length);
      setProcessParameters(processData);
      
      // Load quality attributes
      const qualityData = await fetchQualityAttributes();
      console.log('ParametersBadgeDemo: Quality attributes loaded:', qualityData.length);
      setQualityAttributes(qualityData);
      
    } catch (err) {
      console.error('ParametersBadgeDemo: Error loading data:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleProcessParameterChange = (paramName, checked) => {
    console.log('ParametersBadgeDemo: Process parameter changed:', paramName, checked);
    setSelectedProcessParameters(prev => ({
      ...prev,
      [paramName]: checked
    }));
  };

  const handleQualityAttributeChange = (attrName, checked) => {
    console.log('ParametersBadgeDemo: Quality attribute changed:', attrName, checked);
    setSelectedQualityAttributes(prev => ({
      ...prev,
      [attrName]: checked
    }));
  };

  const getSelectedCounts = () => {
    const selectedProcessCount = Object.values(selectedProcessParameters).filter(Boolean).length;
    const selectedQualityCount = Object.values(selectedQualityAttributes).filter(Boolean).length;
    return { selectedProcessCount, selectedQualityCount };
  };

  const clearSelections = () => {
    setSelectedProcessParameters({});
    setSelectedQualityAttributes({});
  };

  const { selectedProcessCount, selectedQualityCount } = getSelectedCounts();

  if (loading) {
    return (
      <Container className="mt-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading parameters...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">
          <Alert.Heading>Error Loading Data</Alert.Heading>
          <p>{error}</p>
          <Button variant="outline-danger" onClick={loadData}>
            Try Again
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4">
      <Row>
        <Col lg={12}>
          <Card>
            <Card.Header className="bg-primary text-white">
              <h4 className="mb-0">Parameters & Quality Attributes with Badges Demo</h4>
              <small>Demonstrating the new unified badge system for RapidCMC</small>
            </Card.Header>
            <Card.Body>
              
              {/* Stats Summary */}
              <Row className="mb-4">
                <Col md={3}>
                  <Card className="text-center border-primary">
                    <Card.Body>
                      <h5 className="text-primary">{processParameters.length}</h5>
                      <small className="text-muted">Process Parameters</small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="text-center border-success">
                    <Card.Body>
                      <h5 className="text-success">{qualityAttributes.length}</h5>
                      <small className="text-muted">Quality Attributes</small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="text-center border-info">
                    <Card.Body>
                      <h5 className="text-info">{selectedProcessCount}</h5>
                      <small className="text-muted">Selected Process Params</small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="text-center border-warning">
                    <Card.Body>
                      <h5 className="text-warning">{selectedQualityCount}</h5>
                      <small className="text-muted">Selected Quality Attrs</small>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Controls */}
              <Row className="mb-3">
                <Col>
                  <div className="d-flex gap-2 align-items-center">
                    <Button 
                      variant="outline-secondary" 
                      size="sm" 
                      onClick={clearSelections}
                      disabled={selectedProcessCount === 0 && selectedQualityCount === 0}
                    >
                      Clear All Selections
                    </Button>
                    <Button 
                      variant="outline-primary" 
                      size="sm" 
                      onClick={loadData}
                    >
                      Refresh Data
                    </Button>
                    <small className="text-muted ms-auto">
                      Total selected: {selectedProcessCount + selectedQualityCount}
                    </small>
                  </div>
                </Col>
              </Row>

              {/* Tabbed Views */}
              <Tabs
                activeKey={activeKey}
                onSelect={setActiveKey}
                className="mb-3"
              >
                <Tab eventKey="grouped" title="Grouped View">
                  <ParametersWithBadges
                    processParameters={processParameters}
                    qualityAttributes={qualityAttributes}
                    selectedProcessParameters={selectedProcessParameters}
                    selectedQualityAttributes={selectedQualityAttributes}
                    onProcessParameterChange={handleProcessParameterChange}
                    onQualityAttributeChange={handleQualityAttributeChange}
                    showUnified={false}
                  />
                </Tab>
                
                <Tab eventKey="unified" title="Unified View">
                  <ParametersWithBadges
                    processParameters={processParameters}
                    qualityAttributes={qualityAttributes}
                    selectedProcessParameters={selectedProcessParameters}
                    selectedQualityAttributes={selectedQualityAttributes}
                    onProcessParameterChange={handleProcessParameterChange}
                    onQualityAttributeChange={handleQualityAttributeChange}
                    showUnified={true}
                  />
                </Tab>
              </Tabs>

              {/* API Data Preview */}
              <Row className="mt-4">
                <Col md={6}>
                  <Card>
                    <Card.Header>
                      <h6 className="mb-0">Sample Process Parameter API Data</h6>
                    </Card.Header>
                    <Card.Body>
                      {processParameters.length > 0 && (
                        <pre className="small text-muted" style={{ fontSize: '0.75rem' }}>
                          {JSON.stringify(processParameters[0], null, 2)}
                        </pre>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card>
                    <Card.Header>
                      <h6 className="mb-0">Sample Quality Attribute API Data</h6>
                    </Card.Header>
                    <Card.Body>
                      {qualityAttributes.length > 0 && (
                        <pre className="small text-muted" style={{ fontSize: '0.75rem' }}>
                          {JSON.stringify(qualityAttributes[0], null, 2)}
                        </pre>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ParametersBadgeDemo;
